// @ts-nocheck

// App
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

//Actions
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import { csrFetchListingsAndHistogramData } from 'app/shared/pages/SearchResultsPage/clientSideFetchingFunctions';

// Components
import LoadingBanner from './LoadingBanner';
import MapWrapper from 'app/shared/wrappers/MapWrapper/component';
import SlidingPreviewContainer from 'app/shared/modules/map/SlidingPreviewContainer';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';
import CreateSearchAlertModal from 'app/shared/modules/user-search/CreateSearchAlertModal';

// Icons
import IconMap from 'images/icons/map-view.min.svg';
import IconList from 'images/icons/list-outlined.min.svg';

// Tracking

// Misc / utils
import * as S from './styles';
import CONSTANTS from 'app/shared/constants/DispatchConstants';
import { useYieldCallback } from '@zillow/yield-callback';

const MapButton = ({ isMapInitialized, handleInitializeMapClick }) => {
  // Redux
  const dispatch = useDispatch();
  const isGmapLoaded = useSelector((state) => state.app.gmapLoaded);
  const isInitialSsrPage = useSelector((state) => state.app.isInitialSsrPage);
  const previewListing = useSelector((state) => state.listings.listingGroups.previewListing);
  const query = useSelector((state) => state.location.current.query);
  const areaName = useSelector((state) => state.area.area.fullName);
  const showCreateSearchAlert = useSelector((state) => state.app.overlays.createSearchAlert);
  const searchExperience = useSelector((state) => state.view.searchExperience);

  //React refs
  const focusRef = useRef(null);
  const isLoaded = useRef(null);

  // React state
  const [showMap, setShowMap] = useState(false);
  const [isMwebMapLoaded, setIsMwebMapLoaded] = useState(false);

  useEffect(() => {
    if (searchExperience === 'map') {
      handleInitializeMapClick();
      setShowMap(!showMap);
    }
  }, []);

  useEffect(() => {
    /**
     * isLoaded is a component instance variable. Do not set focus on the button until
     * after the component has been interacted with.
     * @see: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
     */
    if (!isLoaded.current) {
      isLoaded.current = true;
    } else {
      if (!showMap) {
        focusRef.current.focus();
        dispatch(ListingEngineActions.clearPreviewAndCurrent());
      }
      dispatch({
        type: CONSTANTS.VIEW_SEARCH_EXPERIENCE,
        payload: showMap ? 'map' : 'list',
      });
    }
  }, [showMap]);

  useEffect(() => {
    const ssrPageWithQuery = Boolean(isInitialSsrPage && query.lat && query.lon);
    if (isGmapLoaded && ssrPageWithQuery) {
      dispatch(csrFetchListingsAndHistogramData(query));
    }
  }, [isGmapLoaded]);

  const complexTask = useYieldCallback(() => {
    if (!isMapInitialized) {
      handleInitializeMapClick();
    }
    setShowMap(!showMap);
  });

  const handleMapButtonClick = () => {
    complexTask();
  };

  const handleHidePopup = () => {
    dispatch(AppActions.toggleOverlay('createSearchAlert', false));
  };

  return (
    <>
      <S.MapButton
        onClick={handleMapButtonClick}
        $previewListingButtonPosition={previewListing && showMap}
        ref={focusRef}
        aria-label={showMap ? 'View listings' : 'View Google Maps'}
      >
        <S.MapIcon src={showMap ? IconList : IconMap} width="14px" height="14px" alt="" />
        {showMap ? 'List' : 'Map'}
      </S.MapButton>

      {isMapInitialized && (
        <S.MapView onClose={handleMapButtonClick} isVisible={showMap}>
          <VisuallyHidden htmlTag="div" role="status" aria-live="polite">
            {`Interactive Google Map showing listings in ${areaName}. Details about each listing, including address and price, can be found in the search results under the h2 heading "Apartments for Rent"`}
          </VisuallyHidden>
          <S.InteriorView>
            <LoadingBanner isMwebMapLoaded={isMwebMapLoaded} />
            <MapWrapper setIsMwebMapLoaded={setIsMwebMapLoaded} />
            {previewListing && <SlidingPreviewContainer listing={previewListing} />}
          </S.InteriorView>
        </S.MapView>
      )}
      {showCreateSearchAlert && (
        <CreateSearchAlertModal
          onHidePopup={handleHidePopup}
          triggerLocation="search_results"
          triggerObject="search_results|alerts_modal"
        />
      )}
    </>
  );
};

export default MapButton;
